import {
  formatCurrency,
  getMagnitudeCurrencyByThresholds,
} from "../COREDesignSystem/Content/COREStats";
// @ts-ignore
import { NumberFormat } from "./globals";

export type PowerUnit = "W" | "kW" | "MW" | "GW" | "TW";
type EnergyUnit = "Wh" | "kWh" | "MWh" | "GWh" | "TWh";

export const WATTS_PER_MW = 1e6;

/** A unit and its divisor, kept apart so a chart can label an axis once and
 *  scale every value against it. */
export type PowerScale = {
  unit: PowerUnit;
  divisor: number;
};

type UnitScale<U extends string> = readonly {
  readonly min: number;
  readonly unit: U;
  readonly divisor: number;
}[];

type NullableNumber = number | null | undefined;

export const NULL_DISPLAY = "-";
const LOCALE = "en-US" as const;

/**
 * Ordered largest → smallest so the first match wins.
 *
 * | Threshold  | Symbol |
 * |------------|--------|
 * | ≥ 1 T      | TW     |
 * | ≥ 1 G      | GW     |
 * | ≥ 1 M      | MW     |
 * | ≥ 1 k      | kW     |
 * | < 1 k      | W      |
 */
const POWER_SCALE: UnitScale<PowerUnit> = [
  { min: 1e12, unit: "TW", divisor: 1e12 },
  { min: 1e9, unit: "GW", divisor: 1e9 },
  { min: 1e6, unit: "MW", divisor: 1e6 },
  { min: 1e3, unit: "kW", divisor: 1e3 },
  { min: 0, unit: "W", divisor: 1 },
] as const;

const ENERGY_SCALE: UnitScale<EnergyUnit> = [
  { min: 1e12, unit: "TWh", divisor: 1e12 },
  { min: 1e9, unit: "GWh", divisor: 1e9 },
  { min: 1e6, unit: "MWh", divisor: 1e6 },
  { min: 1e3, unit: "kWh", divisor: 1e3 },
  { min: 0, unit: "Wh", divisor: 1 },
] as const;

/**
 * Formats a number after dividing by the given divisor.
 * - Integer results → no decimals (`1,000`)
 * - Decimal results → up to 3 decimal places (`9.124`)
 */
const scaleAndFormat = (value: number, divisor: number): string => {
  const scaled = value / divisor;

  return Number.isInteger(scaled)
    ? scaled.toLocaleString(LOCALE)
    : scaled.toLocaleString(LOCALE, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
};

const isNullish = (value: NullableNumber): value is null | undefined =>
  value === null || value === undefined;

/**
 * Generic auto-scaling formatter.
 * Walks the scale table (largest → smallest) and picks the first tier whose
 * `min` value is ≤ the absolute input value.
 */
const autoScale = <U extends string>(
  value: NullableNumber,
  scale: UnitScale<U>
): string => {
  if (isNullish(value)) return NULL_DISPLAY;

  const abs = Math.abs(value);
  const tier = scale.find((s) => abs >= s.min) ?? scale[scale.length - 1];

  return `${scaleAndFormat(value, tier.divisor)} ${tier.unit}`;
};

/**
 * Format a **power** value (base unit: watts) with automatic SI scaling.
 *
 * ```
 * formatPower(9_124_000) // → "9.124 MW"
 * formatPower(null)       // → "-"
 * ```
 */
export const formatPower = (valueInWatts: NullableNumber): string =>
  autoScale(valueInWatts, POWER_SCALE);

/**
 * The unit `formatPower` would pick, as a scale rather than a string — for
 * applying one unit to a whole set of values, as a chart axis must.
 */
export const pickPowerScale = (valueInWatts: number): PowerScale => {
  const abs = Math.abs(valueInWatts);
  const tier =
    POWER_SCALE.find((s) => abs >= s.min) ??
    POWER_SCALE[POWER_SCALE.length - 1];

  return { unit: tier.unit, divisor: tier.divisor };
};

/**
 * Formats at a given scale instead of auto-scaling, so a tooltip can read in
 * the same unit as its axis. `value` and `scale.divisor` must share a base.
 */
export const formatPowerAtScale = (
  value: NullableNumber,
  scale: PowerScale
): string => {
  if (isNullish(value)) return NULL_DISPLAY;

  return `${scaleAndFormat(value, scale.divisor)} ${scale.unit}`;
};

/**
 * Format an **energy** value (base unit: watt-hours) with automatic SI scaling.
 *
 * ```
 * formatEnergy(91_240_000) // → "91.24 MWh"
 * formatEnergy(undefined)  // → "-"
 * ```
 */
export const formatEnergy = (valueInWattHours: NullableNumber): string =>
  autoScale(valueInWattHours, ENERGY_SCALE);

/**
 * Format a **power** value that is already in **MW**.
 *
 * ```
 * formatPowerMW(453) // → "453 MW"
 * formatPowerMW(null) // → "-"
 * ```
 */
export const formatPowerMW = (valueInMw: NullableNumber): string => {
  if (isNullish(valueInMw)) return NULL_DISPLAY;
  return `${scaleAndFormat(valueInMw, 1)} MW`;
};

/**
 * Format an **energy** value that is already in **MWh**.
 *
 * ```
 * formatEnergyMWh(565_650) // → "565,650 MWh"
 * formatEnergyMWh(null)    // → "-"
 * ```
 */
export const formatEnergyMWh = (valueInMwh: NullableNumber): string => {
  if (isNullish(valueInMwh)) return NULL_DISPLAY;
  return `${scaleAndFormat(valueInMwh, 1)} MWh`;
};

/**
 * Format a **power** value that is already in **MW**, displayed as GW.
 *
 * ```
 * formatPowerGW(1500) // → "1.5 GW"
 * formatPowerGW(null) // → "-"
 * ```
 */
export const formatPowerGW = (valueInMw: NullableNumber): string => {
  if (isNullish(valueInMw)) return NULL_DISPLAY;
  return `${scaleAndFormat(valueInMw, 1000)} GW`;
};

/**
 * Format an **energy** value that is already in **MWh**, displayed as GWh.
 *
 * ```
 * formatEnergyGWh(1_500_000) // → "1,500 GWh"
 * formatEnergyGWh(null)      // → "-"
 * ```
 */
export const formatEnergyGWh = (valueInMwh: NullableNumber): string => {
  if (isNullish(valueInMwh)) return NULL_DISPLAY;
  return `${scaleAndFormat(valueInMwh, 1000)} GWh`;
};

/**
 * Format a currency value using the design-system auto-magnitude thresholds.
 *
 * ```
 * formatCurrencyValue(1_500_000)     // → "$1.50m"
 * formatCurrencyValue(42.5)          // → "$42.50"
 * formatCurrencyValue(null)          // → "-"
 * ```
 */
export const formatCurrencyValue = (
  value: NullableNumber,
  currencySymbol: string = "$"
): string => {
  if (isNullish(value)) return NULL_DISPLAY;
  const magnitude = getMagnitudeCurrencyByThresholds(value);
  const sign = value < 0 ? "-" : "";
  return `${sign}${currencySymbol}${formatCurrency(
    Math.abs(value),
    magnitude
  )}`;
};

/**
 * Format a plain dollar amount without magnitude suffixes.
 *
 * Decimal places default to 0 for whole numbers and 2 otherwise; pass
 * `decimalPlaces` to fix them.
 *
 * ```ts
 * formatDollarAmount(132)        // → "$132"
 * formatDollarAmount(67.5)       // → "$67.50"
 * formatDollarAmount(-1234.5, 2) // → "-$1,234.50"
 * ```
 */
export const formatDollarAmount = (
  value: number,
  decimalPlaces?: number
): string => {
  const digits = decimalPlaces ?? (Number.isInteger(value) ? 0 : 2);
  const sign = value < 0 ? "-" : "";
  return `${sign}$${NumberFormat(Math.abs(value), digits)}`;
};

/** Format a fraction as a percentage — `0.855` → `"86%"`, or `"85.5%"` at 3 digits. */
export const formatPercentage = (
  value: NullableNumber,
  maximumFractionDigits: number = 0
): string => {
  if (isNullish(value)) return NULL_DISPLAY;
  return `${(value * 100).toLocaleString(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  })}%`;
};

/**
 * Format a plain numeric value (up to 3 decimal places, comma-separated).
 *
 * ```
 * formatNumericValue(1234) // → "1,234"
 * formatNumericValue(null) // → "-"
 * ```
 */
export const formatNumericValue = (value: NullableNumber): string => {
  if (isNullish(value)) return NULL_DISPLAY;

  return Number.isInteger(value)
    ? value.toLocaleString(LOCALE)
    : value.toLocaleString(LOCALE, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
};

/**
 * Format storage duration (consistently displayed with 3 decimal places).
 * The unit suffix defaults to the short "h"; pass "hours" for the long form.
 *
 * ```
 * formatStorageDuration(1.5)          // → "1.500 h"
 * formatStorageDuration(1.5, "hours") // → "1.500 hours"
 * formatStorageDuration(null)         // → "-"
 * ```
 */
export const formatStorageDuration = (
  valueInHours: NullableNumber,
  unitSuffix: "h" | "hours" = "h"
): string => {
  if (isNullish(valueInHours)) return NULL_DISPLAY;
  return `${valueInHours.toLocaleString(LOCALE, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })} ${unitSuffix}`;
};

/**
 * The formatting kind of a numeric CSV cell:
 * - `price`   → money: always exactly 2 decimals, trailing zeros kept
 * - `percent` → the value is a fraction; export the ×100 figure, then trimmed
 * - `number`  → any other numeric quantity, up to 3 decimals, trailing zeros trimmed
 */
export type CsvNumberKind = "price" | "percent" | "number";

/**
 * Serialize a numeric CSV cell for spreadsheet export: a bare decimal with no
 * unit, symbol, magnitude suffix or thousand separator so Excel can sort it.
 *
 * - `price`   → exactly 2 decimals, trailing zeros kept (17.6 → "17.60").
 * - `percent` → the fraction ×100, then trimmed like `number` (0.176 → "17.6").
 * - `number`  → up to 3 decimals, trailing zeros (and a bare point) trimmed,
 *               so integers show with no decimals (5 → "5", 17.6 → "17.6").
 * - zero (after any ×100) → "-" for every kind.
 * - null / undefined / non-finite → "-".
 */
export const formatCsvNumericCell = (
  value: NullableNumber,
  kind: CsvNumberKind
): string => {
  if (value == null || !Number.isFinite(value)) return NULL_DISPLAY;
  const scaled = kind === "percent" ? value * 100 : value;
  if (kind === "price") {
    const s = scaled.toFixed(2);
    return Number(s) === 0 ? NULL_DISPLAY : s;
  }
  const trimmed = scaled.toFixed(3).replace(/\.?0{1,20}$/, "");
  return trimmed === "" || Number(trimmed) === 0 ? NULL_DISPLAY : trimmed;
};
